import React, { useState, useEffect } from 'react';
import { 
  getOffersConfig, updateOffersConfig, 
  getServicesConfig, updateServicesConfig, 
  getCtaConfig, updateCtaConfig 
} from '../firebase/services';
import toast from 'react-hot-toast';
import { resizeImage } from '../Utils/imageHelpers';

export default function ManageHomeContent() {
  const [loading, setLoading] = useState(false);
  
  // Offers State
  const [offers, setOffers] = useState([]);
  
  // Services State
  const [services, setServices] = useState([]);
  
  // CTA State
  const [cta, setCta] = useState({
    title: '', text: '', buttonText: '', buttonLink: '',
    bannerUrl: '', iconUrl: '', bgUrl: ''
  });
  const [ctaBannerFile, setCtaBannerFile] = useState(null);
  const [ctaIconFile, setCtaIconFile] = useState(null);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [offersData, servicesData, ctaData] = await Promise.all([
        getOffersConfig(),
        getServicesConfig(),
        getCtaConfig()
      ]);

      if (offersData.length > 0) setOffers(offersData);
      else {
        // Initialize with 3 empty offers if none exist
        setOffers([
            { id: 1, subtitle: '', title: '', buttonText: 'Read More', buttonLink: '#', imageUrl: '' },
            { id: 2, subtitle: '', title: '', buttonText: 'Read More', buttonLink: '#', imageUrl: '' },
            { id: 3, subtitle: '', title: '', buttonText: 'Read More', buttonLink: '#', imageUrl: '' }
        ]);
      }

      if (servicesData.length > 0) setServices(servicesData);
      else {
        // Initialize with 4 empty services if none exist
        setServices([
            { id: 1, title: '', text: '', iconUrl: '' },
            { id: 2, title: '', text: '', iconUrl: '' },
            { id: 3, title: '', text: '', iconUrl: '' },
            { id: 4, title: '', text: '', iconUrl: '' }
        ]);
      }

      if (ctaData) setCta(ctaData);
    } catch (error) {
      console.error("Error fetching home content:", error);
      toast.error("Failed to load content");
    } finally {
      setLoading(false);
    }
  };

  // --- OFFERS HANDLERS ---
  const handleOfferChange = (index, field, value) => {
    const newOffers = [...offers];
    newOffers[index][field] = value;
    setOffers(newOffers);
  };

  const handleOfferImageChange = async (index, e) => {
    const file = e.target.files[0];
    if (file) {
      try {
        const resizedFile = await resizeImage(file, 800, 600);
        const newOffers = [...offers];
        newOffers[index].imageFile = resizedFile;
        newOffers[index].imageUrl = URL.createObjectURL(resizedFile);
        setOffers(newOffers);
      } catch (error) {
        toast.error("Error processing image");
      }
    }
  };

  const saveOffers = async () => {
    setLoading(true);
    try {
      await updateOffersConfig(offers);
      toast.success("Offers updated successfully!");
    } catch (error) {
      toast.error("Failed to update offers");
    } finally {
      setLoading(false);
    }
  };

  // --- SERVICES HANDLERS ---
  const handleServiceChange = (index, field, value) => {
    const newServices = [...services];
    newServices[index][field] = value;
    setServices(newServices);
  };

  const handleServiceIconChange = async (index, e) => {
    const file = e.target.files[0];
    if (file) {
      try {
        // Icons can be smaller
        const resizedFile = await resizeImage(file, 200, 200);
        const newServices = [...services];
        newServices[index].iconFile = resizedFile;
        newServices[index].iconUrl = URL.createObjectURL(resizedFile);
        setServices(newServices);
      } catch (error) {
        toast.error("Error processing icon");
      }
    }
  };

  const saveServices = async () => {
    setLoading(true);
    try {
      await updateServicesConfig(services);
      toast.success("Services updated successfully!");
    } catch (error) {
      toast.error("Failed to update services");
    } finally {
      setLoading(false);
    }
  };

  // --- CTA HANDLERS ---
  const handleCtaChange = (e) => {
    const { name, value } = e.target;
    setCta(prev => ({ ...prev, [name]: value }));
  };

  const handleCtaFileChange = async (e, type) => {
    const file = e.target.files[0];
    if (file) {
      try {
        const resizedFile = await resizeImage(file, type === 'banner' ? 1000 : 200, type === 'banner' ? 800 : 200);
        if (type === 'banner') {
            setCtaBannerFile(resizedFile);
            setCta(prev => ({ ...prev, bannerUrl: URL.createObjectURL(resizedFile) }));
        } else {
            setCtaIconFile(resizedFile);
            setCta(prev => ({ ...prev, iconUrl: URL.createObjectURL(resizedFile) }));
        }
      } catch (error) {
        toast.error("Error processing image");
      }
    }
  };

  const saveCta = async () => {
    setLoading(true);
    try {
      await updateCtaConfig(cta, ctaBannerFile, ctaIconFile);
      toast.success("CTA section updated successfully!");
    } catch (error) {
      toast.error("Failed to update CTA");
    } finally {
      setLoading(false);
    }
  };

  if (loading && offers.length === 0) return <div style={{padding: '40px', textAlign: 'center'}}>Loading...</div>;

  return (
    <div className="admin-container">
      <div className="admin-header">
        <h2>Home Page Content</h2>
        <p>Manage the content sections of your home page</p>
      </div>
      
      {/* OFFERS SECTION */}
      <div className="admin-card">
        <div className="card-header">
          <h3><ion-icon name="pricetags-outline"></ion-icon> Offers Section (3 Cards)</h3>
        </div>
        <div className="form-grid" style={{gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))'}}>
          {offers.map((offer, index) => (
            <div key={index} className="admin-card" style={{boxShadow: 'none', border: '1px solid #e1e3e5', padding: '16px', marginBottom: 0}}>
              <h4 style={{marginTop: 0, marginBottom: '16px', fontSize: '1rem'}}>Offer Card {index + 1}</h4>
              <div className="input-group">
                <label>Subtitle</label>
                <input className="custom-input" value={offer.subtitle} onChange={(e) => handleOfferChange(index, 'subtitle', e.target.value)} placeholder="e.g. Selected Items" />
              </div>
              <div className="input-group">
                <label>Title</label>
                <input className="custom-input" value={offer.title} onChange={(e) => handleOfferChange(index, 'title', e.target.value)} placeholder="e.g. Hot Summer Deals" />
              </div>
              <div className="input-group">
                <label>Button Text</label>
                <input className="custom-input" value={offer.buttonText} onChange={(e) => handleOfferChange(index, 'buttonText', e.target.value)} />
              </div>
              <div className="input-group">
                <label>Button Link</label>
                <input className="custom-input" value={offer.buttonLink} onChange={(e) => handleOfferChange(index, 'buttonLink', e.target.value)} />
              </div>
              <div className="input-group">
                <label>Background Image</label>
                {offer.imageUrl && <img src={offer.imageUrl} alt="Preview" style={{width: '100%', height: '150px', objectFit: 'cover', marginBottom: '10px', borderRadius: '5px'}} />}
                <input type="file" onChange={(e) => handleOfferImageChange(index, e)} accept="image/*" />
              </div>
            </div>
          ))}
        </div>
        <div style={{marginTop: '20px', textAlign: 'right'}}>
            <button className="btn-primary" onClick={saveOffers} disabled={loading}>Save Offers</button>
        </div>
      </div>

      {/* SERVICES SECTION */}
      <div className="admin-card">
        <div className="card-header">
          <h3><ion-icon name="construct-outline"></ion-icon> Services Section (4 Items)</h3>
        </div>
        <div className="form-grid" style={{gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))'}}>
          {services.map((service, index) => (
            <div key={index} className="admin-card" style={{boxShadow: 'none', border: '1px solid #e1e3e5', padding: '16px', marginBottom: 0}}>
              <h4 style={{marginTop: 0, marginBottom: '16px', fontSize: '1rem'}}>Service {index + 1}</h4>
              <div className="input-group">
                <label>Title</label>
                <input className="custom-input" value={service.title} onChange={(e) => handleServiceChange(index, 'title', e.target.value)} placeholder="e.g. Free Delivery" />
              </div>
              <div className="input-group">
                <label>Description</label>
                <textarea className="custom-textarea" rows="3" value={service.text} onChange={(e) => handleServiceChange(index, 'text', e.target.value)} placeholder="Service details..." />
              </div>
              <div className="input-group">
                <label>Icon</label>
                {service.iconUrl && <img src={service.iconUrl} alt="Icon" style={{width: '50px', height: '50px', objectFit: 'contain', marginBottom: '10px'}} />}
                <input type="file" onChange={(e) => handleServiceIconChange(index, e)} accept="image/*" />
              </div>
            </div>
          ))}
        </div>
        <div style={{marginTop: '20px', textAlign: 'right'}}>
            <button className="btn-primary" onClick={saveServices} disabled={loading}>Save Services</button>
        </div>
      </div>

      {/* CTA SECTION */}
      <div className="admin-card">
        <div className="card-header">
          <h3><ion-icon name="megaphone-outline"></ion-icon> Call to Action (CTA) Section</h3>
        </div>
        <div className="form-grid">
            <div>
                <div className="input-group">
                    <label>Title</label>
                    <input className="custom-input" name="title" value={cta.title} onChange={handleCtaChange} />
                </div>
                <div className="input-group">
                    <label>Text</label>
                    <textarea className="custom-textarea" name="text" rows="4" value={cta.text} onChange={handleCtaChange} />
                </div>
                <div className="input-group">
                    <label>Button Text</label>
                    <input className="custom-input" name="buttonText" value={cta.buttonText} onChange={handleCtaChange} />
                </div>
                <div className="input-group">
                    <label>Button Link</label>
                    <input className="custom-input" name="buttonLink" value={cta.buttonLink} onChange={handleCtaChange} />
                </div>
            </div>
            <div>
                <div className="input-group">
                    <label>Banner Image (Right Side)</label>
                    {cta.bannerUrl && <img src={cta.bannerUrl} alt="Banner" style={{width: '100%', maxHeight: '200px', objectFit: 'contain', marginBottom: '10px', border: '1px solid #e5e7eb', borderRadius: '4px'}} />}
                    <input type="file" onChange={(e) => handleCtaFileChange(e, 'banner')} accept="image/*" />
                </div>
                <div className="input-group">
                    <label>Small Icon (Above Title)</label>
                    {cta.iconUrl && <img src={cta.iconUrl} alt="Icon" style={{width: '100px', objectFit: 'contain', marginBottom: '10px', border: '1px solid #e5e7eb', borderRadius: '4px'}} />}
                    <input type="file" onChange={(e) => handleCtaFileChange(e, 'icon')} accept="image/*" />
                </div>
            </div>
        </div>
        <div style={{marginTop: '20px', textAlign: 'right'}}>
            <button className="btn-primary" onClick={saveCta} disabled={loading}>Save CTA</button>
        </div>
      </div>

    </div>
  );
}
